import { memo, useCallback, useEffect, useState } from 'react';

import { DEBOUNCE_MS } from '@/components/features/UsersList/constants';
import BaseMultiAutocomplete from '@/components/shared/Autocomplete/BaseMultiAutocomplete';
import { BaseMultiAutocompleteProps } from '@/components/shared/Autocomplete/types';
import type { AdminUserAutocompleteResult } from '@/hooks/autocomplete/useAdminUsersAutocomplete';
import { useAdminUsersAutocomplete } from '@/hooks/autocomplete/useAdminUsersAutocomplete';

export interface UserAdminAutocompleteProps {
  value: AdminUserAutocompleteResult[];
  onChange: BaseMultiAutocompleteProps<AdminUserAutocompleteResult>['autocomplete']['onChange'];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  enabled?: boolean;
  width?: string | number;
}

function UserAdminAutocomplete({
  value,
  onChange,
  label = 'Performed By (Admin Emails)',
  placeholder = 'Enter at least 3 characters to search',
  disabled = false,
  enabled = true,
  width,
}: UserAdminAutocompleteProps) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(inputValue), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const { data, isLoading } = useAdminUsersAutocomplete({
    filter: debouncedValue,
    enabled: enabled && !disabled,
  });

  const handleInputChange = useCallback((_: React.SyntheticEvent, input: string) => {
    setInputValue(input);
  }, []);

  const getOptionLabel = useCallback((option: AdminUserAutocompleteResult) => option.email ?? '', []);

  const getOptionDisabled = useCallback((option: AdminUserAutocompleteResult) => value.some((v) => v.email === option.email), [value]);

  const isOptionEqualToValue = useCallback(
    (option: AdminUserAutocompleteResult, val: AdminUserAutocompleteResult) => option.email === val.email,
    [],
  );

  return (
    <BaseMultiAutocomplete<AdminUserAutocompleteResult>
      autocomplete={{
        options: data ?? [],
        value,
        inputValue,
        onInputChange: handleInputChange,
        onChange,
        getOptionLabel,
        getOptionDisabled,
        isOptionEqualToValue,
        filterOptions: (x) => x,
        label,
        placeholder,
        loading: isLoading,
        disabled,
        width,
      }}
    />
  );
}

export default memo(UserAdminAutocomplete);
